import React, {useState, useEffect, useCallback, useMemo} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {apiService, Device, Server, Protocol} from '../../services/api_url';
import {useUserData} from '../../services/useUserData';
import {ObfuscatedStrings} from '../../services/obfyscatedStrings'
import {useAppStore} from '../../store/useAppStore';
import Loader from '../../widgets/Loader/Loader';
import BackgroundCanvasWithoutRadar from '../../widgets/canvas/BackgroundCanvasWithoutRadar';
import {BackIcon} from '../../assets/images/BackIcon';
import {WireGuardIcon, AmneziaWgIcon, VlessIcon} from '../../assets/images/ProtocolIcons';
import {
    PendingDeviceIcon,
    SettingsIcon,
    StatsIcon,
    ProtocolIcon,
} from '../../assets/images/DeviceIcons';
import './DeviceDetailPage.scss';

type NoticeState = {
    type: 'success' | 'error' | 'info';
    text: string;
} | null;


function DeviceDetailPage() {
    const {deviceId} = useParams<{ deviceId: string }>();
    const navigate = useNavigate();
    const {telegramId, loading: userLoading} = useUserData();
    const {devices: cachedDevices, loadDevices, refreshAllData, updateDeviceName} = useAppStore();

    // Локальное состояние для специфичных данных
    const [device, setDevice] = useState<Device | null>(null);
    const [config, setConfig] = useState<string | null>(null);
    const [configFilename, setConfigFilename] = useState<string>('config.conf');
    const [configCopied, setConfigCopied] = useState<boolean>(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
    const [deleting, setDeleting] = useState<boolean>(false);
    const [showSetup, setShowSetup] = useState<boolean>(false);
    const [servers, setServers] = useState<Server[]>([]);
    const [protocols, setProtocols] = useState<Protocol[]>([]);
    const [selectedProtocol, setSelectedProtocol] = useState<number | null>(null);
    const [selectedServer, setSelectedServer] = useState<number | null>(null);
    const [creating, setCreating] = useState<boolean>(false);
    const [loadingConfig, setLoadingConfig] = useState<boolean>(false);
    const [notice, setNotice] = useState<NoticeState>(null);
    const [pressedAction, setPressedAction] = useState<string | null>(null);
    const [localLoading, setLocalLoading] = useState<boolean>(true);
    const [localError, setLocalError] = useState<string | null>(null);
    const [loadingServers, setLoadingServers] = useState<boolean>(false);

    const [showEditName, setShowEditName] = useState<boolean>(false);
    const [newDeviceName, setNewDeviceName] = useState<string>('');
    const [editingName, setEditingName] = useState<boolean>(false);
    const [canEditName, setCanEditName] = useState<boolean>(false);

    const isLoading = userLoading || localLoading;

    // Используем кэшированные устройства для поиска текущего
    const findDeviceInCache = useCallback(() => {
        if (!cachedDevices?.devices || !deviceId) return null;
        return cachedDevices.devices.find(d => d.id === parseInt(deviceId)) || null;
    }, [cachedDevices, deviceId]);

    const showNotice = useCallback((type: 'success' | 'error' | 'info', text: string) => {
        setNotice({type, text});
    }, []);

    const loadConfig = useCallback(async (devId: number) => {
        if (!telegramId) return;

        setLoadingConfig(true);
        try {
            const response = await apiService.getDeviceConfig(devId, telegramId);
            setConfig(response.config);
            setConfigFilename(response.filename);
        } catch (err) {
            console.error('Ошибка загрузки конфига:', err);
            showNotice('error', 'Не удалось загрузить конфигурацию');
        } finally {
            setLoadingConfig(false);
        }
    }, [telegramId, showNotice]);

    // Загрузка устройства
    useEffect(() => {
        const loadDeviceData = async () => {
            if (!telegramId || !deviceId) return;

            setLocalLoading(true);
            setLocalError(null);

            try {
                // Просто получаем все устройства с сервера
                const devicesResponse = await apiService.getUserDevices(telegramId);
                const foundDevice = devicesResponse?.devices.find(d => d.id === parseInt(deviceId));

                if (!foundDevice) {
                    setLocalError('Устройство не найдено');
                    setLocalLoading(false);
                    return;
                }

                setDevice(foundDevice);

                if (foundDevice.is_configured) {
                    await loadConfig(foundDevice.id);
                }
            } catch (err) {
                console.error('Ошибка загрузки устройства:', err);
                setLocalError('Не удалось загрузить устройство');
            } finally {
                setLocalLoading(false);
            }
        };

        loadDeviceData();
    }, [telegramId, deviceId]);

    // Загрузка протоколов (один раз)
    useEffect(() => {
        const loadProtocolsList = async () => {
            try {
                const data = await apiService.getProtocols();
                setProtocols(data);
            } catch (err) {
                console.error('Ошибка загрузки протоколов:', err);
            }
        };

        loadProtocolsList();
    }, []);

    // Эффект для очистки уведомлений
    useEffect(() => {
        if (!notice) return;
        const timer = window.setTimeout(() => setNotice(null), 3500);
        return () => window.clearTimeout(timer);
    }, [notice]);

    // Настройка Telegram WebApp
    useEffect(() => {
        const tg = (window as any).Telegram?.WebApp;
        if (tg) {
            tg.ready();
            tg.expand();
            tg.setHeaderColor?.('#000000');
            tg.setBackgroundColor?.('#000000');
        }
    }, []);

    const animatePress = useCallback((key: string, cb: () => void, delay = 120) => {
        setPressedAction(key);
        window.setTimeout(() => {
            cb();
            window.setTimeout(() => {
                setPressedAction((prev) => (prev === key ? null : prev));
            }, 180);
        }, delay);
    }, []);

    const loadServers = useCallback(async (protocolId: number) => {
        setLoadingServers(true);  // Включаем лоадер
        try {
            const data = await apiService.getServersByProtocol(protocolId);
            setServers(data);
        } catch (err) {
            console.error('Ошибка загрузки серверов:', err);
            showNotice('error', 'Не удалось загрузить серверы');
        } finally {
            setLoadingServers(false);  // Выключаем лоадер
        }
    }, [showNotice]);

    const handleCopyConfig = useCallback(async () => {
        if (!config) return;
        await navigator.clipboard.writeText(config);
        setConfigCopied(true);
        showNotice('success', 'Конфигурация скопирована');
        setTimeout(() => setConfigCopied(false), 1800);
    }, [config, showNotice]);

    const handleShareOnMobile = useCallback(async () => {
        if (!config) return;

        if (navigator.share) {
            try {
                const file = new File([config], configFilename, {type: 'text/plain'});
                await navigator.share({
                    title: ObfuscatedStrings.getCodeConfiguration(),
                    text: ObfuscatedStrings.getCodeConfigurationRus(),
                    files: [file]
                });
            } catch (err: any) {
                if (err.name === 'AbortError' || err.message?.includes('abort') || err.message?.includes('cancel')) {
                    return;
                }
                try {
                    await navigator.clipboard.writeText(config);
                    showNotice('success', 'Конфигурация скопирована');
                } catch {
                    showNotice('error', 'Не удалось поделиться конфигурацией');
                }
            }
        } else {
            try {
                await navigator.clipboard.writeText(config);
                showNotice('success', 'Конфигурация скопирована');
            } catch {
                showNotice('error', 'Не удалось скопировать конфигурацию');
            }
        }
    }, [config, configFilename, showNotice]);

    const handleDownloadConfig = useCallback(() => {
        if (!config) return;

        const blob = new Blob([config], {type: 'text/plain'});
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = configFilename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }, [config, configFilename]);

    const handleDeleteDevice = useCallback(async () => {
        if (!device || !telegramId) return;

        setDeleting(true);
        try {
            await apiService.deleteDevice(device.id, telegramId);

            // Перезагружаем устройство после удаления
            const devicesResponse = await apiService.getUserDevices(telegramId);
            const updatedDevice = devicesResponse?.devices.find(d => d.id === device.id);

            if (updatedDevice) {
                // Устройство теперь пустое (is_configured = false)
                setDevice(updatedDevice);
                setConfig(null); // Очищаем конфиг
                setConfigFilename('config.conf');
            } else {
                // Устройство полностью удалено
                setDevice(null);
                setConfig(null);
                // Перенаправляем на список устройств
                navigate('/devices');
            }

            showNotice('success', 'Устройство успешно удалено');
            setShowDeleteConfirm(false);
        } catch (err: any) {
            console.error('Ошибка удаления устройства:', err);

            let errorMessage = 'Ошибка при удалении устройства';
            if (err.response?.status === 400) {
                errorMessage = 'Невозможно удалить VLESS устройство, пока оно не активировано в приложении';
            }
            showNotice('error', errorMessage);
        } finally {
            setDeleting(false);
        }
    }, [device, telegramId, showNotice, navigate]);

    const handleSetupDevice = useCallback(() => {
        setShowSetup(true);
    }, []);

    const handleCreateVlessDevice = useCallback(async () => {
        if (!device || !telegramId) {
            showNotice('error', 'Устройство не найдено');
            return;
        }

        setCreating(true);
        try {
            const result = await apiService.createVlessDevice(device.id, telegramId);

            if (result.success) {
                showNotice('success', 'VLESS устройство успешно создано');

                // Обновляем данные
                await refreshAllData(telegramId);

                // Перезагружаем устройства, чтобы получить обновленные данные
                const updatedDevices = await loadDevices(telegramId);
                const updatedDevice = updatedDevices?.devices.find(d => d.id === device.id);

                if (updatedDevice) {
                    setDevice(updatedDevice);
                    if (updatedDevice.is_configured && updatedDevice.path_or_url) {
                        // Для VLESS config - это subscription_url
                        setConfig(updatedDevice.path_or_url);
                        setConfigFilename(`vless_${device.id}.txt`);
                    }
                }

                setShowSetup(false);
                setSelectedProtocol(null);
            } else {
                showNotice('error', result.message || 'Ошибка при создании VLESS устройства');
            }
        } catch (err: any) {
            console.error('Ошибка создания VLESS:', err);
            const errorMessage = err.response?.data?.detail || 'Ошибка при создании VLESS устройства';
            showNotice('error', errorMessage);
        } finally {
            setCreating(false);
        }
    }, [device, telegramId, refreshAllData, loadDevices, showNotice]);

    const handleCreateWgAwgDevice = useCallback(async () => {
        if (!device || !selectedServer || !telegramId) return;

        setCreating(true);
        try {
            await apiService.createWgDevice(device.id, selectedServer, telegramId);
            showNotice('success', 'Устройство успешно создано');

            // Обновляем данные
            await refreshAllData(telegramId);

            // Находим обновленное устройство в кэше
            const updatedDevices = await loadDevices(telegramId);
            const updatedDevice = updatedDevices?.devices.find(d => d.id === device.id);
            if (updatedDevice) {
                setDevice(updatedDevice);
                if (updatedDevice.is_configured) {
                    await loadConfig(updatedDevice.id);
                }
            }

            setShowSetup(false);
            setSelectedProtocol(null);
            setSelectedServer(null);
        } catch (err) {
            console.error('Ошибка создания устройства:', err);
            showNotice('error', 'Ошибка при создании устройства');
        } finally {
            setCreating(false);
        }
    }, [device, selectedServer, telegramId, refreshAllData, loadDevices, loadConfig, showNotice]);

    const handleProtocolSelect = useCallback(async (protocolId: number) => {
        setSelectedProtocol(protocolId);
        if (protocolId === 3) {
            setSelectedServer(null);
            // Не вызываем сразу создание, даем время на обновление состояния
            // Создание будет вызвано после того, как пользователь подтвердит
            // Но для VLESS мы создаем сразу, но нужно убедиться что device есть
            if (device) {
                await handleCreateVlessDevice();
            } else {
                showNotice('error', 'Устройство не загружено');
            }
        } else {
            await loadServers(protocolId);
        }
    }, [loadServers, device, handleCreateVlessDevice, showNotice]);

    const handleInstructions = useCallback(() => {
        window.location.href = 'https://appnet.shadownet.nl/install';
    }, []);

    const extractQuickConnectLink = useCallback((configText: string): string | null => {
        if (!configText) return null;

        const trimmed = configText.trim();

        if (/^(happ|vless|vmess|trojan|ss|ssr|hysteria|hy2|tuic):\/\//i.test(trimmed)) {
            return trimmed;
        }

        const match = trimmed.match(/(happ|vless|vmess|trojan|ss|ssr|hysteria|hy2|tuic):\/\/\S+/i);
        if (match) {
            return match[0];
        }

        return null;
    }, []);

    const openCustomScheme = useCallback((url: string) => {
        try {
            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            iframe.src = url;
            document.body.appendChild(iframe);
            window.setTimeout(() => {
                if (iframe.parentNode) {
                    iframe.parentNode.removeChild(iframe);
                }
            }, 1200);
        } catch (error) {
            console.error('iframe open error:', error);
        }

        window.setTimeout(() => {
            try {
                window.location.href = url;
            } catch (error) {
                console.error('location href open error:', error);
            }
        }, 80);
    }, []);

    const handleQuickConnect = useCallback(() => {
        if (!config) {
            showNotice('error', 'Конфигурация ещё не загружена');
            return;
        }

        const quickLink = extractQuickConnectLink(config);

        if (!quickLink) {
            showNotice('error', 'Ссылка для быстрого подключения не найдена');
            return;
        }

        // Только обычные HTTP/HTTPS ссылки
        const isPlainUrl = /^https?:\/\//i.test(quickLink);

        if (!isPlainUrl) {
            showNotice('error', 'Быстрое подключение доступно только для обычных ссылок');
            return;
        }

        const tg = (window as any).Telegram?.WebApp;

        try {
            if (tg?.openLink) {
                tg.openLink(quickLink);
                return;
            }

            window.open(quickLink, '_blank');
        } catch (error) {
            console.error('Ошибка быстрого подключения:', error);
            showNotice('error', 'Не удалось открыть ссылку');
        }
    }, [config, extractQuickConnectLink, showNotice]);

    const handleIncyConnect = useCallback(() => {
        if (!config) {
            showNotice('error', 'Конфигурация ещё не загружена');
            return;
        }

        const trimmed = config.trim();

        // Проверяем, что это обычная HTTP/HTTPS ссылка
        if (!/^https?:\/\//i.test(trimmed)) {
            showNotice('error', 'Ссылка должна быть обычной HTTP/HTTPS ссылкой');
            return;
        }

        // Извлекаем ключ (последнюю часть URL после последнего /)
        const urlParts = trimmed.split('/');
        const key = urlParts[urlParts.length - 1];

        if (!key) {
            showNotice('error', 'Не удалось извлечь ключ из ссылки');
            return;
        }

        const incyUrl = `https://appnet.shadownet.nl/incy/${key}`;

        const tg = (window as any).Telegram?.WebApp;

        try {
            if (tg?.openLink) {
                tg.openLink(incyUrl);
                return;
            }

            window.location.href = incyUrl;
        } catch (error) {
            showNotice('error', 'Не удалось открыть incy');
        }
    }, [config, showNotice]);

    const handleHappConnect = useCallback(() => {
        if (!config) {
            showNotice('error', 'Конфигурация ещё не загружена');
            return;
        }

        const trimmed = config.trim();

        if (!/^https?:\/\//i.test(trimmed)) {
            showNotice('error', 'Ссылка должна быть обычной HTTP/HTTPS ссылкой');
            return;
        }

        const urlParts = trimmed.split('/');
        const key = urlParts[urlParts.length - 1];

        if (!key) {
            showNotice('error', 'Не удалось извлечь ключ из ссылки');
            return;
        }

        const happUrl = `https://appnet.shadownet.nl/happ/${key}`;

        const tg = (window as any).Telegram?.WebApp;

        try {
            if (tg?.openLink) {
                tg.openLink(happUrl);
                return;
            }

            window.location.href = happUrl;
        } catch (error) {
            showNotice('error', 'Не удалось открыть happ');
        }
    }, [config, showNotice]);

    // Мемоизированные вычисления
    const formatDate = useCallback((dateString: string): string => {
        const date = new Date(dateString);
        return date.toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }, []);

    const getProtocolDisplayName = useCallback((protocol?: string | null) => {
        if (!protocol) return 'Не выбран';
        const lower = protocol.toLowerCase();
        if (lower === 'awg') return 'AmneziaWG';
        if (lower === 'wireguard') return 'WireGuard';
        return protocol;
    }, []);

    const getStatusInfo = useCallback(() => {
        if (!device) return {color: '', text: ''};

        const expiresAt = new Date(device.expires_at);
        const now = new Date();
        const isExpired = expiresAt < now;

        if (!device.is_configured) {
            return {
                color: isExpired ? 'red' : 'yellow',
                text: isExpired ? 'Просрочено, требуется настройка' : 'Требуется настройка'
            };
        }

        return {
            color: isExpired ? 'red' : 'green',
            text: isExpired ? 'Подписка истекла' : 'Активно'
        };
    }, [device]);

    const getProtocolIcon = useCallback(() => {
        if (!device) return <StatsIcon size={44} color="#5CFFE1"/>;

        const status = getStatusInfo();
        const isActive = status.color === 'green';
        const isConfigured = device.is_configured;

        if (device.protocol_id === 3) {
            if (isConfigured && isActive) {
                return <StatsIcon size={44} color="#5CFFE1"/>;
            }
            return <PendingDeviceIcon size={44} color="#FFC107"/>;
        }

        if (!isConfigured || !isActive) {
            return <PendingDeviceIcon size={44} color="#FFC107"/>;
        }

        return <StatsIcon size={44} color="#5CFFE1"/>;
    }, [device, getStatusInfo]);

    const isMobile = useCallback((): boolean => {
        const tg = (window as any).Telegram?.WebApp;
        if (tg?.platform) {
            const mobilePlatforms = ['ios', 'android', 'android_x', 'ios_webview'];
            return mobilePlatforms.includes(tg.platform);
        }

        const ua = navigator.userAgent.toLowerCase();
        const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|windows phone|opera mini|iemobile|mobile/i;
        return mobileRegex.test(ua);
    }, []);

    // Проверка возможности редактирования названия
    useEffect(() => {
        if (device) {
            // Можно редактировать только если устройство настроено И есть имя в БД
            const canEdit = device.is_configured &&
                device.name !== null &&
                device.name !== undefined &&
                device.name !== '';
            setCanEditName(canEdit);
        }
    }, [device]);

// Функция для открытия модалки редактирования
    const handleEditNameClick = useCallback(() => {
        if (device && canEditName) {
            setNewDeviceName(device.name || '');
            setShowEditName(true);
        }
    }, [device, canEditName]);

// Функция для сохранения названия
    const handleSaveDeviceName = useCallback(async () => {
        if (!device || !telegramId) return;

        const trimmedName = newDeviceName.trim();
        if (trimmedName.length < 1) {
            showNotice('error', 'Название не может быть пустым');
            return;
        }
        if (trimmedName.length > 50) {
            showNotice('error', 'Название не может превышать 50 символов');
            return;
        }

        setEditingName(true);
        try {
            const result = await apiService.updateDeviceName(device.id, telegramId, trimmedName);
            if (result.success) {
                showNotice('success', 'Название успешно изменено');

                // Обновляем локальное состояние
                setDevice(prev => prev ? {...prev, name: trimmedName} : null);

                // Обновляем кэш в store
                updateDeviceName(device.id, trimmedName);

                setShowEditName(false);
            } else {
                showNotice('error', result.message || 'Ошибка при изменении названия');
            }
        } catch (err: any) {
            console.error('Ошибка изменения названия:', err);
            const errorMessage = err.response?.data?.detail || 'Ошибка при изменении названия';
            showNotice('error', errorMessage);
        } finally {
            setEditingName(false);
        }
    }, [device, telegramId, newDeviceName, showNotice, updateDeviceName]);

    // Ранние возвраты после всех хуков
    if (isLoading) {
        return (
            <div className="tg-app">
                <BackgroundCanvasWithoutRadar/>
                <div className="device-detail-page device-detail-loading">
                    <Loader/>
                </div>
            </div>
        );
    }

    if (!telegramId) {
        return (
            <div className="tg-app">
                <BackgroundCanvasWithoutRadar/>
                <div className="device-detail-page">
                    <div className="device-detail-error-container">
                        <div className="device-detail-error-icon">🔒</div>
                        <h2>Необходима авторизация</h2>
                        <p>Войдите в систему для управления устройствами</p>
                        <button className="device-detail-continue-btn" onClick={() => navigate('/key')}>
                            Перейти к авторизации
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (localError || !device) {
        return (
            <div className="tg-app">
                <BackgroundCanvasWithoutRadar/>
                <div className="device-detail-page">
                    <div className="device-detail-error-container">
                        <div className="device-detail-error-icon">❌</div>
                        <h2>Устройство не найдено</h2>
                        <p>{localError || 'Устройство не существует или было удалено'}</p>
                        <button className="device-detail-continue-btn" onClick={() => navigate('/devices')}>
                            Вернуться к устройствам
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const status = getStatusInfo();


    return (
        <div className="tg-app">
            <BackgroundCanvasWithoutRadar/>

            <div className="device-detail-page">
                <div className="device-detail-shell">
                    <div className="device-detail-page-header">
                        <button className="back-button" onClick={() => navigate('/devices')}>
                            <BackIcon/>
                        </button>
                        <div className="device-detail-head-copy">
                            <h1 className="device-detail-page-title">Устройство</h1>
                            <p className="device-detail-page-subtitle">Детали подключения и управление конфигурацией</p>
                        </div>
                    </div>

                    {notice && (
                        <div className={`device-detail-notice device-detail-notice-${notice.type}`}>
                            <span className="device-detail-notice-dot"/>
                            <span>{notice.text}</span>
                        </div>
                    )}

                    <div className={`device-detail-card device-detail-card-${status.color}`}>
                        <div className="device-detail-card-icon">
                            {getProtocolIcon()}
                        </div>
                        <div className="device-detail-card-info">
                            <div className="device-detail-card-info-edit">
                                <h2 className="device-detail-card-name">{device.name || 'Без названия'}</h2>
                                <div className={`device-detail-card-status device-detail-status-${status.color}`}>
                                    {status.text}
                                </div>
                            </div>
                            {canEditName ? (
                                <button
                                    className="device-detail-edit-name-btn"
                                    onClick={handleEditNameClick}
                                    title="Редактировать название"
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                         stroke="currentColor" strokeWidth="2">
                                        <path d="M17 3l4 4-11 11H6v-4L17 3z"/>
                                        <path d="M14 6l4 4"/>
                                    </svg>
                                </button>
                            ) : (
                                <button
                                    className="device-detail-edit-name-btn disabled"
                                    onClick={() => {
                                        showNotice('info', 'Редактирование названия доступно только для настроенных устройств, у которых уже есть название');
                                    }}
                                    title="Редактирование недоступно"
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                         stroke="currentColor" strokeWidth="2">
                                        <path d="M17 3l4 4-11 11H6v-4L17 3z"/>
                                        <path d="M14 6l4 4"/>
                                    </svg>
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="device-detail-info-section">
                        <div className="device-detail-section-head">
                            <div className="device-detail-section-title">Информация</div>
                        </div>

                        <div className="device-detail-info-list">
                            <div className="device-detail-info-row">
                                <span className="device-detail-info-label">ID</span>
                                <span className="device-detail-info-value">{device.id}</span>
                            </div>

                            <div className="device-detail-info-row">
                                <span className="device-detail-info-label">Протокол</span>
                                <span className="device-detail-info-value device-detail-info-protocol">
                                    <ProtocolIcon size={14} color="#5CFFE1"/>
                                    <span>{getProtocolDisplayName(device.protocol_name)}</span>
                                </span>
                            </div>

                            {device.protocol_id !== 3 && device.server_name && (
                                <div className="device-detail-info-row">
                                    <span className="device-detail-info-label">Сервер</span>
                                    <span className="device-detail-info-value">{device.server_name}</span>
                                </div>
                            )}

                            <div className="device-detail-info-row">
                                <span className="device-detail-info-label">Истекает</span>
                                <span className="device-detail-info-value">{formatDate(device.expires_at)}</span>
                            </div>
                        </div>
                    </div>

                    {device.is_configured && config && (
                        <div className="device-detail-config-section">
                            <div className="device-detail-section-head config">
                                <div className="device-detail-section-title">Конфигурация</div>
                                <div className="device-detail-config-actions">
                                    {device.protocol_id === 3 && (
                                        <button
                                            className={`device-detail-config-btn ${configCopied ? 'device-detail-config-copied' : ''}`}
                                            onClick={handleCopyConfig}
                                            title="Копировать"
                                        >
                                            {configCopied ? (
                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                     strokeWidth="2.5">
                                                    <path d="M20 6L9 17L4 12" strokeLinecap="round"
                                                          strokeLinejoin="round"/>
                                                </svg>
                                            ) : (
                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                     strokeWidth="2">
                                                    <rect x="9" y="9" width="13" height="13" rx="2"/>
                                                    <rect x="2" y="2" width="13" height="13" rx="2"/>
                                                </svg>
                                            )}
                                        </button>
                                    )}

                                    {device.protocol_id !== 3 && !isMobile() && (
                                        <button
                                            className="device-detail-config-btn"
                                            onClick={handleDownloadConfig}
                                            title="Скачать"
                                        >
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                                <polyline points="7 10 12 15 17 10"/>
                                                <line x1="12" y1="15" x2="12" y2="3"/>
                                            </svg>
                                        </button>
                                    )}

                                    <button
                                        className="device-detail-config-btn"
                                        onClick={handleShareOnMobile}
                                        title="Поделиться"
                                    >
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <circle cx="18" cy="5" r="3"/>
                                            <circle cx="6" cy="12" r="3"/>
                                            <circle cx="18" cy="19" r="3"/>
                                            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
                                            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                                        </svg>
                                    </button>
                                </div>
                            </div>

                            {loadingConfig ? (
                                <div className="device-detail-config-loading">
                                    <div className="device-detail-loader-small"></div>
                                    <span>Загрузка...</span>
                                </div>
                            ) : (
                                <div className="device-detail-config-preview">
                                    <div className="device-detail-config-scroll">
                                        <pre className="device-detail-config-text">{config}</pre>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {device.is_configured && device.protocol_id === 3 && (
                        <div className="triple-buttons">
                            {/*<button*/}
                            {/*    className={`device-detail-action-btn device-detail-action-primary device-detail-action-pressable ${pressedAction === 'quick' ? 'is-pressed' : ''}`}*/}
                            {/*    onClick={() => animatePress('quick', handleQuickConnect)}*/}
                            {/*>*/}
                            {/*    <span>Быстрое подключение</span>*/}
                            {/*</button>*/}

                            <button
                                className={`device-detail-action-btn device-detail-action-primary device-detail-action-pressable ${pressedAction === 'incy' ? 'is-pressed' : ''}`}
                                onClick={() => animatePress('incy', handleIncyConnect)}
                            >
                                <span>Быстрое подключение incy</span>
                            </button>

                            <button
                                className={`device-detail-action-btn device-detail-action-primary device-detail-action-pressable ${pressedAction === 'happ' ? 'is-pressed' : ''}`}
                                onClick={() => animatePress('happ', handleHappConnect)}
                            >
                                <span>Быстрое подключение Happ</span>
                            </button>
                        </div>
                    )}

                    <div className="device-detail-actions-single">
                        {device.is_configured ? (
                            <button
                                className={`device-detail-action-btn device-detail-action-danger device-detail-action-pressable ${pressedAction === 'delete' ? 'is-pressed' : ''}`}
                                onClick={() => animatePress('delete', () => setShowDeleteConfirm(true), 90)}
                            >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18"
                                     height="18">
                                    <path
                                        d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                    <line x1="10" y1="11" x2="10" y2="17"/>
                                    <line x1="14" y1="11" x2="14" y2="17"/>
                                </svg>
                                <span>Удалить устройство</span>
                            </button>
                        ) : (
                            <button
                                className={`device-detail-action-btn device-detail-action-primary device-detail-action-pressable ${pressedAction === 'setup' ? 'is-pressed' : ''}`}
                                onClick={() => animatePress('setup', handleSetupDevice)}
                            >
                                <SettingsIcon size={18} color="white"/>
                                <span>Настроить устройство</span>
                            </button>
                        )}
                    </div>
                </div>

                {showDeleteConfirm && (
                    <div className="device-detail-modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
                        <div className="device-detail-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="device-detail-modal-header">
                                <h3>Удалить устройство?</h3>
                                <button className="device-detail-modal-close"
                                        onClick={() => setShowDeleteConfirm(false)}>✕
                                </button>
                            </div>

                            <div className="device-detail-modal-body">
                                <p className="device-detail-confirm-text">
                                    Устройство <strong>{device.name || 'Без названия'}</strong> будет удалено без
                                    возможности быстрого восстановления.
                                </p>

                                <div className="device-detail-confirm-actions">
                                    <button
                                        className="device-detail-action-btn device-detail-action-secondary"
                                        onClick={() => setShowDeleteConfirm(false)}
                                        disabled={deleting}
                                    >
                                        Отмена
                                    </button>

                                    <button
                                        className="device-detail-action-btn device-detail-action-danger"
                                        onClick={handleDeleteDevice}
                                        disabled={deleting}
                                    >
                                        {deleting ? 'Удаление...' : 'Удалить'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {showSetup && (
                    <div className="device-detail-modal-overlay" onClick={() => setShowSetup(false)}>
                        <div className="device-detail-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="device-detail-modal-header">
                                <h3>Настройка устройства</h3>
                                <button className="device-detail-modal-close" onClick={() => setShowSetup(false)}>✕
                                </button>
                            </div>

                            <div className="device-detail-modal-body">
                                {!selectedProtocol ? (
                                    <>
                                        <p className="device-detail-setup-text">Выберите протокол для подключения:</p>

                                        <div className="device-detail-protocol-grid">
                                            {/* .filter(protocol => protocol.id === 3) эту хуйню надо будет удалить если появятся снова вг и авг*/}
                                            {protocols.filter(protocol => protocol.id === 3).map((protocol) => {
                                                const getProtocolIconComponent = () => {
                                                    // if (protocol.name.toLowerCase() === 'wireguard') return <WireGuardIcon />;
                                                    // if (protocol.name.toLowerCase() === 'awg') return <AmneziaWgIcon />;
                                                    if (protocol.name === 'Vless') return <VlessIcon/>;
                                                    return null;
                                                };

                                                return (
                                                    <button
                                                        key={protocol.id}
                                                        className="device-detail-protocol-btn"
                                                        onClick={() => handleProtocolSelect(protocol.id)}
                                                        disabled={creating}
                                                    >
                                                        <div className="device-detail-protocol-icon">
                                                            {getProtocolIconComponent()}
                                                        </div>
                                                        <span className="device-detail-protocol-name">
                                                            {getProtocolDisplayName(protocol.name)}
                                                        </span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </>
                                ) : selectedProtocol === 3 ? (
                                    <div className="device-detail-creating-state">
                                        <div className="device-detail-loader"></div>
                                        <p>Создание VLESS подключения...</p>
                                    </div>
                                ) : (
                                    <>
                                        <div className="device-detail-back-block">
                                            <button
                                                className="back-btn"
                                                onClick={() => {
                                                    setSelectedProtocol(null);
                                                    setSelectedServer(null);
                                                    setServers([]);
                                                }}
                                                disabled={creating}
                                            >
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                                                     stroke="currentColor" strokeWidth="2">
                                                    <path d="M19 12H5M12 19l-7-7 7-7" strokeLinecap="round"
                                                          strokeLinejoin="round"/>
                                                </svg>
                                            </button>

                                            <div className="device-detail-setup-text">
                                                Выберите сервер для
                                                протокола {getProtocolDisplayName(protocols.find((p) => p.id === selectedProtocol)?.name)}:
                                            </div>
                                        </div>

                                        {/* Показываем лоадер, пока загружаются серверы */}
                                        {loadingServers ? (
                                            <div className="device-detail-servers-loading">
                                                <div className="device-detail-loader"></div>
                                                <p>Загрузка серверов...</p>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="device-detail-servers-list">
                                                    {servers.map((server) => {
                                                        const usedSlots = server.used_slots || 0;
                                                        const totalSlots = server.max_slots || 100;

                                                        return (
                                                            <button
                                                                key={server.id}
                                                                className={`device-detail-server-btn ${selectedServer === server.id ? 'device-detail-server-selected' : ''} ${server.free_slots <= 0 ? 'device-detail-server-full' : ''}`}
                                                                onClick={() => setSelectedServer(server.id)}
                                                                disabled={server.free_slots <= 0 || creating}
                                                            >
                                                                <div className="device-detail-server-info">
                                                                    <span
                                                                        className="device-detail-server-name">{server.name}</span>
                                                                </div>

                                                                <span
                                                                    className={`device-detail-server-slots ${server.free_slots <= 0 ? 'device-detail-slots-full' : ''}`}>
                                                    {server.free_slots <= 0 ? 'Заполнен' : `${usedSlots}/${totalSlots}`}
                                                </span>
                                                            </button>
                                                        );
                                                    })}
                                                </div>

                                                {selectedServer && (
                                                    <button
                                                        className="device-detail-action-btn device-detail-action-primary device-detail-create-btn"
                                                        onClick={handleCreateWgAwgDevice}
                                                        disabled={creating}
                                                    >
                                                        {creating ? 'Создание...' : 'Создать устройство'}
                                                    </button>
                                                )}
                                            </>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {showEditName && (
                    <div className="device-detail-modal-overlay" onClick={() => setShowEditName(false)}>
                        <div className="device-detail-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="device-detail-modal-header">
                                <h3>Редактирование названия</h3>
                                <button className="device-detail-modal-close" onClick={() => setShowEditName(false)}>✕
                                </button>
                            </div>

                            <div className="device-detail-modal-body">
                                <div className="device-detail-edit-name-field">
                                    <label>Название устройства</label>
                                    <input
                                        type="text"
                                        className="device-detail-name-input"
                                        value={newDeviceName}
                                        onChange={(e) => setNewDeviceName(e.target.value)}
                                        maxLength={50}
                                        placeholder="Введите новое название"
                                        autoFocus
                                    />
                                    <div className="device-detail-name-hint">
                                        {newDeviceName.length}/50 символов
                                    </div>
                                </div>

                                <div className="device-detail-confirm-actions">
                                    <button
                                        className="device-detail-action-btn device-detail-action-secondary"
                                        onClick={() => setShowEditName(false)}
                                        disabled={editingName}
                                    >
                                        Отмена
                                    </button>
                                    <button
                                        className="device-detail-action-btn device-detail-action-primary"
                                        onClick={handleSaveDeviceName}
                                        disabled={editingName || !newDeviceName.trim()}
                                    >
                                        {editingName ? 'Сохранение...' : 'Сохранить'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default DeviceDetailPage;
